import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { IStringMap } from 'app/interfaces/IStringMap';
import { getValue } from 'app/extensibility/utils/property-bag.util';
import { Constants } from 'app/shared/shared-constants';
import {
    akvToModel,
    akvToResource,
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';
import {
    AdapterContext,
    azureDefaultAuthRefTypes,
    AzureKeyVault,
    azureStorageAuthTypes,
    ConnectionAuthKind,
    Credential,
    CredentialTypeOptions,
    type IModel,
    type ITridentResource,
    ReferenceAdapter,
    servicePrincipalCredentialTypes
} from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureBlobStorageLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                connectionString: (_context: AdapterContext, _value: unknown, ls: AzureBlobStorageLinkedService) => {
                    if (
                        ls &&
                        !ls.fullyByJsonEditor &&
                        ls.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication &&
                        !ls.enableAccountAKV
                    ) {
                        if (ls.accountKey instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                accountKey: 'secret',
                accountKind: '*',
                sasUri: 'secret',
                sasToken: 'secret',
                serviceEndpoint: '*',
                tenant: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                servicePrincipalCredentialType: '*',
                servicePrincipalCredential: 'secret',
                encryptedCredential: '*',
                azureCloudType: '*',
                credential: '*',
                containerUri: '*',
                authenticationType: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureBlobStorageLinkedServiceAdapter extends LinkedServiceAdapter<AzureBlobStorageLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureBlobStorageLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationMethod;
        if (model.credential) {
            model.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;

            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = azureStorageAuthTypes.ServicePrincipalAuthentication;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = azureStorageAuthTypes.UserAssignedManagedIdentity;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = azureStorageAuthTypes.UserAuth;
            }
        }
        if (model.isExternalConnection && model.extension._credentialType$ === ConnectionAuthKind.OAuth2) {
            // Trident OAuth2
            finalAuthType = azureStorageAuthTypes.UserAuth;
            model.authReferenceMethod = undefined;
        }
        model.authenticationMethod = finalAuthType;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.AzureBlobStorageLinkedServiceTypeProperties> => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        switch (credentialType) {
            case ConnectionAuthKind.Key:
                const connectionDetails: Record<'account' | 'domain', string> = JSON.parse(
                    tridentResource.connectionDetails
                );
                return {
                    connectionString:
                        `DefaultEndpointsProtocol=https;AccountName=${connectionDetails.account ?? ''};EndpointSuffix=${connectionDetails.domain?.split('.').slice(1).join('.') ?? ''}`
                };
            case ConnectionAuthKind.SAS:
                return {
                    sasUri: datasourceReference.path
                };
            case ConnectionAuthKind.Anonymous:
                return {
                    authenticationType: azureStorageAuthTypes.Anonymous,
                    containerUri: datasourceReference.path
                };
            case ConnectionAuthKind.ServicePrincipal:
                return {
                    serviceEndpoint: datasourceReference.path,
                    // a fake ID
                    servicePrincipalId: '000'
                };
            case ConnectionAuthKind.OAuth2:
                return {
                    serviceEndpoint: datasourceReference.path
                };
            case ConnectionAuthKind.WorkspaceIdentity:
                return {
                    wiUrl: datasourceReference.path
                }
            default:
                return undefined;
        }
    };

    protected modelConstructor(): new () => AzureBlobStorageLinkedService {
        return AzureBlobStorageLinkedService;
    }

    protected model(): IModel {
        return AzureBlobStorageLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: AzureBlobStorageLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureBlobStorageLinkedService;

        properties.type = Metamodel.modelFor(AzureBlobStorageLinkedService).name;

        if (linkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication) {
            if (linkedService.enableSasUriAKV && linkedService.referedSasUriAKV instanceof AzureKeyVault) {
                properties.typeProperties = {
                    sasUri: akvToResource(linkedService.referedSasUriAKV, this.context)
                };
            } else {
                let sasUri = this.valueToResource(linkedService, AzureBlobStorageLinkedService.SAS_URI);
                if (linkedService.sasToken && isString(linkedService.sasToken)) {
                    let sasToken = linkedService.sasToken as string;
                    if (sasUri.endsWith('?')) {
                        sasUri = sasUri.substr(0, sasUri.length - 1);
                    }
                    if (sasToken.startsWith('?')) {
                        sasToken = sasToken.substr(1);
                    }
                    properties.typeProperties = {
                        sasUri: sasUri + '?' + sasToken
                    };
                } else {
                    properties.typeProperties = {
                        sasUri,
                        sasToken: parameterizablePasswordOrAKVtoSecretBase(
                            linkedService,
                            AzureBlobStorageLinkedService.SAS_TOKEN,
                            this.context
                        )
                    };
                }
            }
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication) {
            if (!linkedService.enableAccountAKV) {
                const propertiesBag: IStringMap<string | Model.SecretBase | undefined> = {};
                propertiesBag['DefaultEndpointsProtocol'] = 'https';
                propertiesBag['AccountName'] = this.valueToResource(
                    linkedService,
                    AzureBlobStorageLinkedService.ACCOUNT_NAME
                );
                if (linkedService.primaryEndpoint) {
                    // can't check `isPartitionedDns` property as azure subscription doesn't have this checkbox
                    const primaryEndpoint = this.valueToResource(
                        linkedService,
                        AzureBlobStorageLinkedService.PRIMARY_ENDPOINT
                    );
                    if (primaryEndpoint) {
                        propertiesBag['BlobEndpoint'] = primaryEndpoint;
                        // if user changed existing LS from old to new, so endpointSuffix should be removed
                        // as they should be mutually exclusive
                        delete linkedService.endpointSuffix;
                    }
                } else {
                    const endpointSuffix = this.valueToResource(
                        linkedService,
                        AzureBlobStorageLinkedService.ENDPOINT_SUFFIX
                    );
                    if (endpointSuffix) {
                        propertiesBag['EndpointSuffix'] = endpointSuffix;
                        // if user changed existing LS from new to old, so primaryEndpoint should be removed
                        // as they should be mutually exclusive
                        delete linkedService.primaryEndpoint;
                    }
                }
                if (linkedService.accountKey && !(linkedService.accountKey instanceof AzureKeyVault)) {
                    propertiesBag['AccountKey'] = linkedService.accountKey;
                } else if (!linkedService.accountKey) {
                    propertiesBag['AccountKey'] = parameterizablePasswordOrAKVtoSecretBase(
                        linkedService,
                        AzureBlobStorageLinkedService.ACCOUNT_KEY,
                        this.context
                    );
                }

                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        if (pair.value != null) {
                            propertiesBag[pair.key] = pair.value;
                        }
                    });
                }

                let connectionString = '';
                Object.keys(propertiesBag).forEach((key) => {
                    const value = getValue(propertiesBag, key);
                    if (value != null) {
                        connectionString += `${key}=${value};`;
                    }
                });
                properties.typeProperties = {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    connectionString:
                        linkedService.accountName === Constants.maskedCredential &&
                            linkedService.accountKey === Constants.maskedCredential
                            ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            ({
                                type: 'SecureString',
                                value: Constants.maskedCredential
                            } as Model.SecureString)
                            : this._connectionStringToResource(connectionString),
                    accountKey:
                        linkedService.accountKey instanceof AzureKeyVault
                            ? akvToResource(linkedService.accountKey, this.context)
                            : !linkedService.accountKey
                                ? propertiesBag['AccountKey']
                                : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAccountAKV, this.context)
                };
            }
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication) {
            properties.typeProperties = {
                serviceEndpoint: this.valueToResource(linkedService, AzureBlobStorageLinkedService.SERVICE_ENDPOINT),
                accountKind: linkedService.accountKind
            };
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.ServicePrincipalAuthentication) {
            const azureCloudType = linkedService.azureCloudType;
            const isCredential =
                linkedService.authReferenceMethod === azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
            properties.typeProperties = {
                azureCloudType,
                serviceEndpoint: this.valueToResource(linkedService, AzureBlobStorageLinkedService.SERVICE_ENDPOINT),
                tenant: this.valueToResource(linkedService, AzureBlobStorageLinkedService.TENANT),
                servicePrincipalId: !isCredential
                    ? this.valueToResource(linkedService, AzureBlobStorageLinkedService.SERVICE_PRINCIPAL_ID)
                    : undefined,
                servicePrincipalKey:
                    !isCredential && linkedService.isOldServicePrincipalResource
                        ? parameterizablePasswordOrAKVtoSecretBase(
                            linkedService,
                            AzureBlobStorageLinkedService.SERVICE_PRINCIPAL_KEY,
                            this.context
                        )
                        : undefined,
                servicePrincipalCredentialType:
                    !isCredential && !linkedService.isOldServicePrincipalResource
                        ? linkedService.servicePrincipalCredentialType
                        : undefined,
                servicePrincipalCredential:
                    !isCredential && !linkedService.isOldServicePrincipalResource
                        ? linkedService.servicePrincipalCredentialType ===
                            servicePrincipalCredentialTypes.ServicePrincipalCert && linkedService.servicePrincipalCert
                            ? akvToResource(linkedService.servicePrincipalCert, this.context)
                            : parameterizablePasswordOrAKVtoSecretBase(
                                linkedService,
                                AzureBlobStorageLinkedService.SERVICE_PRINCIPAL_KEY,
                                this.context
                            )
                        : undefined,
                credential:
                    isCredential && linkedService.credential
                        ? (ReferenceAdapter.toResource(
                            linkedService.credential,
                            this.context
                        ) as Model.CredentialReference)
                        : undefined,
                accountKind: linkedService.accountKind
            };
        } else if (
            linkedService.authenticationMethod === azureStorageAuthTypes.UserAssignedManagedIdentity ||
            linkedService.authenticationMethod === azureStorageAuthTypes.UserAuth
        ) {
            properties.typeProperties = {
                serviceEndpoint: this.valueToResource(linkedService, AzureBlobStorageLinkedService.SERVICE_ENDPOINT),
                accountKind: linkedService.accountKind,
                credential: linkedService.credential
                    ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                    : undefined
            };
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.Anonymous) {
            properties.typeProperties = {
                containerUri: this.valueToResource(linkedService, AzureBlobStorageLinkedService.CONTAINER_URI),
                authenticationType: linkedService.authenticationMethod
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: AzureBlobStorageLinkedService
    ): Promise<AzureBlobStorageLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureBlobStorageLinkedService;
        linkedService.reset();
        linkedService.authenticationMethod = azureStorageAuthTypes.AccountKeyAuthentication;
        linkedService.additionalFields = [];

        if (properties.typeProperties.wiUrl != null) {
            linkedService.authenticationMethod = azureStorageAuthTypes.WorkspaceIdentity;
        } else if (properties.typeProperties.sasUri != null) {
            linkedService.authenticationMethod = azureStorageAuthTypes.SasUriAuthentication;
            if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                linkedService.sasUri = Constants.maskedCredential;
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureBlobStorageLinkedService.SAS_TOKEN,
                    properties.typeProperties.sasToken, this.context
                );
            } else if ((properties.typeProperties.sasUri as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.enableSasUriAKV = true;
                linkedService.referedSasUriAKV = akvToModel(
                    properties.typeProperties.sasUri as Model.AzureKeyVaultSecretReference, this.context
                );
            } else {
                // string & parameter
                linkedService.enableSasUriAKV = false;
                ValueAdapter.toModel(
                    linkedService,
                    AzureBlobStorageLinkedService.SAS_URI,
                    properties.typeProperties.sasUri
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureBlobStorageLinkedService.SAS_TOKEN,
                    properties.typeProperties.sasToken, this.context
                );
            }
        } else if (properties.typeProperties.serviceEndpoint) {
            ValueAdapter.toModel(
                linkedService,
                AzureBlobStorageLinkedService.SERVICE_ENDPOINT,
                properties.typeProperties.serviceEndpoint
            );
            if (properties.typeProperties.accountKind) {
                linkedService.accountKind = properties.typeProperties.accountKind;
            }
            if (properties.typeProperties.servicePrincipalId) {
                linkedService.azureCloudType = properties.typeProperties.azureCloudType;
                linkedService.authenticationMethod = azureStorageAuthTypes.ServicePrincipalAuthentication;
                ValueAdapter.toModel(
                    linkedService,
                    AzureBlobStorageLinkedService.TENANT,
                    properties.typeProperties.tenant
                );
                ValueAdapter.toModel(
                    linkedService,
                    AzureBlobStorageLinkedService.SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.servicePrincipalId
                );

                if (properties.typeProperties.servicePrincipalCredentialType != null) {
                    linkedService.isOldServicePrincipalResource = false;
                    linkedService.servicePrincipalCredentialType =
                        properties.typeProperties.servicePrincipalCredentialType;
                    if (
                        linkedService.servicePrincipalCredentialType ===
                        servicePrincipalCredentialTypes.ServicePrincipalKey
                    ) {
                        parameterizableSecretBaseToModel(
                            linkedService,
                            AzureBlobStorageLinkedService.SERVICE_PRINCIPAL_KEY,
                            properties.typeProperties.servicePrincipalCredential, this.context
                        );
                    } else if (
                        linkedService.servicePrincipalCredentialType ===
                        servicePrincipalCredentialTypes.ServicePrincipalCert
                    ) {
                        linkedService.servicePrincipalCert = akvToModel(
                            properties.typeProperties.servicePrincipalCredential as Model.SecretBase, this.context
                        );
                    }
                } else {
                    linkedService.isOldServicePrincipalResource = true;
                    parameterizableSecretBaseToModel(
                        linkedService,
                        AzureBlobStorageLinkedService.SERVICE_PRINCIPAL_KEY,
                        properties.typeProperties.servicePrincipalKey, this.context
                    );
                }

                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential, this.context
                );
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
            } else {
                linkedService.authenticationMethod = azureStorageAuthTypes.MSIAuthentication;
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
            }
        } else if (isString(properties.typeProperties.connectionString)) {
            linkedService.accountKey = Constants.maskedCredential;
            const keyValPairs = (properties.typeProperties.connectionString as string).split(';');
            keyValPairs.forEach((item) => {
                // assume key doesn't contain '='
                const sepIndex = item.indexOf('=');
                if (sepIndex >= 0) {
                    const key = item.substr(0, sepIndex);
                    const val = item.substr(sepIndex + 1);
                    switch (key) {
                        case 'AccountName':
                            ValueAdapter.toModel(linkedService, AzureBlobStorageLinkedService.ACCOUNT_NAME, val);
                            break;
                        case 'EndpointSuffix':
                            ValueAdapter.toModel(linkedService, AzureBlobStorageLinkedService.ENDPOINT_SUFFIX, val);
                            break;
                        case 'AccountKey':
                            parameterizableSecretBaseToModel(
                                linkedService,
                                AzureBlobStorageLinkedService.ACCOUNT_KEY,
                                properties.typeProperties.accountKey, this.context
                            );
                            break;
                        case 'DefaultEndpointsProtocol':
                            if (val !== 'https') {
                                linkedService.additionalFields.push({ key, value: val });
                            }
                            break;
                        case 'BlobEndpoint':
                            ValueAdapter.toModel(linkedService, AzureBlobStorageLinkedService.PRIMARY_ENDPOINT, val);
                            break;
                        default:
                            linkedService.additionalFields.push({ key, value: val });
                    }
                }
            });
            linkedService.isPartitionedDns = !!linkedService.primaryEndpoint;

            if (properties.typeProperties.accountKey) {
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureBlobStorageLinkedService.ACCOUNT_KEY,
                    properties.typeProperties.accountKey, this.context
                );
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.accountName = Constants.maskedCredential;
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureBlobStorageLinkedService.ACCOUNT_KEY,
                    properties.typeProperties.accountKey, this.context
                );
            }
        } else if (
            properties.typeProperties.connectionString &&
            (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret'
        ) {
            linkedService.referedAccountAKV = akvToModel(
                properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context
            );
            linkedService.enableAccountAKV = true;
        } else if (properties.typeProperties.authenticationType === azureStorageAuthTypes.Anonymous) {
            linkedService.authenticationMethod = azureStorageAuthTypes.Anonymous;
            ValueAdapter.toModel(
                linkedService,
                AzureBlobStorageLinkedService.CONTAINER_URI,
                properties.typeProperties.containerUri
            );
        }

        return linkedService;
    }
}
